import { useState, useEffect } from 'preact/hooks';
import {
  fetchAkbContent,
  createAkbContent,
  updateAkbContent,
  deleteAkbContent,
  reembedAkbContent,
  type AkbContentRow,
} from '../api/admin';

const selectStyle = {
  padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-medium)',
  background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '12px',
};

const labelStyle = {
  display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px', marginTop: '10px',
};

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border-medium)',
  background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box' as const,
};

const ENV_OPTIONS = ['', 'prod', 'beta', 'cloud'] as const;
const TIER_OPTIONS = ['', 'free', 'valko', 'enterprise'] as const;
const SCOPE_LABEL: Record<string, string> = {
  prod: 'prod', beta: 'beta', cloud: 'cloud',
  free: 'free', valko: 'valko', enterprise: 'enterprise',
};

type PersonaKey = 'cloud-free' | 'beta-valko' | 'prod-user' | 'prod-admin';
const PERSONAS: { key: PersonaKey; label: string; env: string; tier: string; isAdmin: boolean }[] = [
  { key: 'cloud-free', label: 'Cloud / Free', env: 'cloud', tier: 'free', isAdmin: false },
  { key: 'beta-valko', label: 'Beta / Valko', env: 'beta', tier: 'valko', isAdmin: false },
  { key: 'prod-user', label: 'Prod / User', env: 'prod', tier: 'valko', isAdmin: false },
  { key: 'prod-admin', label: 'Prod / Admin', env: 'prod', tier: 'valko', isAdmin: true },
];

function scopeMatchesPersona(
  row: AkbContentRow,
  env: string, tier: string, isAdmin: boolean,
): boolean {
  if (!isAdmin && row.adminOnly) return false;
  if (row.scopeEnv !== null && row.scopeEnv !== env) return false;
  if (row.scopeTier !== null && row.scopeTier !== tier) return false;
  return true;
}

const BLANK: Omit<AkbContentRow, 'id' | 'createdAt' | 'updatedAt'> = {
  title: '', body: '', scopeEnv: null, scopeTier: null,
  userId: null, priority: 0, adminOnly: false,
};

export function AkbKnowledgeTab() {
  const [items, setItems] = useState<AkbContentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filterEnv, setFilterEnv] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [filterAdminOnly, setFilterAdminOnly] = useState<boolean | null>(null);

  // Edit/create modal
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...BLANK });
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Reembed
  const [reembedding, setReembedding] = useState<string | null>(null);

  // Import
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');

  // Preview
  const [previewPersona, setPreviewPersona] = useState<PersonaKey | null>(null);

  const load = () => {
    setLoading(true);
    setError('');
    fetchAkbContent()
      .then(setItems)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const visibleItems = items.filter((item) => {
    if (filterEnv && item.scopeEnv !== filterEnv) return false;
    if (filterTier && item.scopeTier !== filterTier) return false;
    if (filterAdminOnly !== null && item.adminOnly !== filterAdminOnly) return false;
    return true;
  });

  const openCreate = () => {
    setEditId(null);
    setForm({ ...BLANK });
    setShowModal(true);
  };

  const openEdit = (item: AkbContentRow) => {
    setEditId(item.id);
    setForm({
      title: item.title, body: item.body,
      scopeEnv: item.scopeEnv, scopeTier: item.scopeTier,
      userId: item.userId, priority: item.priority, adminOnly: item.adminOnly,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      setError('Title and body are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editId) {
        await updateAkbContent(editId, form);
      } else {
        await createAkbContent(form);
      }
      setShowModal(false);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (deleteConfirm !== id) {
      setDeleteConfirm(id);
      return;
    }
    setDeleteConfirm(null);
    setError('');
    try {
      await deleteAkbContent(id);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleReembed = async (id: string) => {
    setReembedding(id);
    setError('');
    try {
      const result = await reembedAkbContent(id);
      if (!result.embedded) setError(`Reembed skipped for ${id} (Ollama unavailable?)`);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setReembedding(null);
    }
  };

  const handleImport = async () => {
    if (!importText.trim()) return;
    const lines = importText.split('\n');
    let title = '';
    const bodyLines: string[] = [];
    for (const line of lines) {
      if (!title && line.startsWith('#')) {
        title = line.replace(/^#+\s*/, '').trim();
      } else {
        bodyLines.push(line);
      }
    }
    if (!title) title = 'Imported content';
    const body = bodyLines.join('\n').trim() || importText.trim();
    setForm({ ...BLANK, title, body });
    setEditId(null);
    setShowImport(false);
    setShowModal(true);
  };

  const scopeBadge = (row: AkbContentRow) => {
    const parts: string[] = [];
    if (row.scopeEnv) parts.push(row.scopeEnv);
    if (row.scopeTier) parts.push(row.scopeTier);
    if (row.adminOnly) parts.push('admin-only');
    if (parts.length === 0) return <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>universal</span>;
    return (
      <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {parts.map((p) => (
          <span key={p} style={{
            fontSize: 10, padding: '1px 6px', borderRadius: 10,
            background: p === 'admin-only'
              ? 'color-mix(in srgb, var(--accent-purple) 28%, var(--bg-elevated))'
              : 'color-mix(in srgb, var(--accent-cyan) 28%, var(--bg-elevated))',
            color: p === 'admin-only' ? 'var(--accent-purple)' : 'var(--accent-cyan)',
          }}>{p}</span>
        ))}
      </span>
    );
  };

  const previewPersonaData = previewPersona ? PERSONAS.find((p) => p.key === previewPersona)! : null;
  const previewVisible = previewPersonaData
    ? items.filter((i) => scopeMatchesPersona(i, previewPersonaData.env, previewPersonaData.tier, previewPersonaData.isAdmin))
    : [];

  return (
    <div class="akb-knowledge-tab" style={{ padding: '0 0 40px' }}>
      {error && <div class="message error" style={{ margin: '0 0 12px' }}>{error}</div>}

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        <button class="btn-resume" onClick={openCreate}>+ New Entry</button>
        <button class="btn-secondary" onClick={() => setShowImport(true)}>Import from Markdown</button>

        <select
          value={filterEnv}
          onChange={(e) => setFilterEnv((e.target as HTMLSelectElement).value)}
          style={selectStyle}
        >
          <option value="">All envs</option>
          {ENV_OPTIONS.filter(Boolean).map((v) => <option key={v} value={v}>{v}</option>)}
        </select>

        <select
          value={filterTier}
          onChange={(e) => setFilterTier((e.target as HTMLSelectElement).value)}
          style={selectStyle}
        >
          <option value="">All tiers</option>
          {TIER_OPTIONS.filter(Boolean).map((v) => <option key={v} value={v}>{v}</option>)}
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-primary)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={filterAdminOnly === true}
            onChange={(e) => setFilterAdminOnly((e.target as HTMLInputElement).checked ? true : null)}
          />
          Admin-only only
        </label>

        <div style={{ marginLeft: 'auto' }}>
          <select
            value={previewPersona ?? ''}
            onChange={(e) => setPreviewPersona(((e.target as HTMLSelectElement).value as PersonaKey) || null)}
            style={selectStyle}
          >
            <option value="">Preview as…</option>
            {PERSONAS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
      </div>

      {/* Preview panel */}
      {previewPersonaData && (
        <div style={{ background: 'var(--bg-surface)', borderRadius: 8, padding: 12, marginBottom: 12, border: '1px solid var(--border-medium)' }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
            Preview: <strong style={{ color: 'var(--accent-purple)' }}>{previewPersonaData.label}</strong>
            — {previewVisible.length} of {items.length} rows visible
          </div>
          {previewVisible.length === 0 ? (
            <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>No content visible for this persona</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {previewVisible.map((r) => (
                <div key={r.id} style={{ fontSize: 12, color: 'var(--text-primary)', padding: '4px 8px', background: 'var(--bg-elevated)', borderRadius: 4 }}>
                  ✓ {r.title} {scopeBadge(r)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Content list */}
      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading…</div>
      ) : visibleItems.length === 0 ? (
        <div style={{ color: 'var(--text-dim)', fontSize: 13 }}>
          {items.length === 0 ? 'No AKB content yet. Create your first entry.' : 'No entries match the current filters.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {visibleItems.map((item) => (
            <div key={item.id} style={{
              background: 'var(--bg-elevated)', borderRadius: 8, padding: '10px 14px',
              border: '1px solid var(--border-medium)', display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{item.title}</span>
                  {scopeBadge(item)}
                  {item.priority > 0 && (
                    <span style={{ fontSize: 10, color: 'var(--accent-green)' }}>p{item.priority}</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.body.slice(0, 120)}{item.body.length > 120 ? '…' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button class="btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => openEdit(item)}>
                  Edit
                </button>
                <button
                  class="btn-secondary"
                  style={{ fontSize: 11, padding: '4px 10px' }}
                  disabled={reembedding === item.id}
                  onClick={() => handleReembed(item.id)}
                  title="Regenerate semantic embedding"
                >
                  {reembedding === item.id ? '…' : '↻'}
                </button>
                <button
                  class={deleteConfirm === item.id ? 'btn-kill' : 'btn-secondary'}
                  style={{ fontSize: 11, padding: '4px 10px' }}
                  onClick={() => handleDelete(item.id)}
                  title={deleteConfirm === item.id ? 'Click again to confirm' : 'Delete'}
                >
                  {deleteConfirm === item.id ? 'Confirm' : '✕'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit modal */}
      {showModal && (
        <div class="modal-overlay" onClick={() => setShowModal(false)}>
          <div class="modal-content" style={{ maxWidth: 560, width: '95vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>{editId ? 'Edit AKB Entry' : 'New AKB Entry'}</h3>

            <label style={labelStyle}>Title</label>
            <input
              type="text"
              value={form.title}
              onInput={(e) => setForm((f) => ({ ...f, title: (e.target as HTMLInputElement).value }))}
              style={inputStyle}
              placeholder="e.g. What is AKB?"
            />

            <label style={labelStyle}>Body (Markdown)</label>
            <textarea
              value={form.body}
              onInput={(e) => setForm((f) => ({ ...f, body: (e.target as HTMLTextAreaElement).value }))}
              style={{ ...inputStyle, minHeight: 160, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
              placeholder="Content to inject into the AI's context…"
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              <div>
                <label style={labelStyle}>Scope: Environment</label>
                <select
                  value={form.scopeEnv ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, scopeEnv: (e.target as HTMLSelectElement).value || null }))}
                  style={inputStyle}
                >
                  <option value="">Universal (all envs)</option>
                  <option value="prod">prod</option>
                  <option value="beta">beta</option>
                  <option value="cloud">cloud</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Scope: Tier</label>
                <select
                  value={form.scopeTier ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, scopeTier: (e.target as HTMLSelectElement).value || null }))}
                  style={inputStyle}
                >
                  <option value="">Universal (all tiers)</option>
                  <option value="free">free</option>
                  <option value="valko">valko</option>
                  <option value="enterprise">enterprise</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              <div>
                <label style={labelStyle}>Priority (higher = earlier)</label>
                <input
                  type="number"
                  value={form.priority}
                  min={0}
                  onInput={(e) => setForm((f) => ({ ...f, priority: Number((e.target as HTMLInputElement).value) }))}
                  style={inputStyle}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.adminOnly}
                    onChange={(e) => setForm((f) => ({ ...f, adminOnly: (e.target as HTMLInputElement).checked }))}
                  />
                  Admin-only (hidden from regular users)
                </label>
              </div>
            </div>

            {error && <div class="message error" style={{ margin: '10px 0 0' }}>{error}</div>}

            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button class="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button class="btn-resume" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import modal */}
      {showImport && (
        <div class="modal-overlay" onClick={() => setShowImport(false)}>
          <div class="modal-content" style={{ maxWidth: 520, width: '95vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Import from Markdown</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 10px' }}>
              Paste markdown below (or upload a .md file). The first heading becomes the title; the rest becomes the body.
            </p>
            <input
              type="file"
              accept=".md,.txt"
              style={{ marginBottom: 8, fontSize: 12 }}
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (ev) => setImportText(String(ev.target?.result ?? ''));
                reader.readAsText(file);
              }}
            />
            <textarea
              value={importText}
              onInput={(e) => setImportText((e.target as HTMLTextAreaElement).value)}
              style={{ ...inputStyle, minHeight: 200, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
              placeholder="# My AKB Title&#10;&#10;Content goes here…"
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button class="btn-secondary" onClick={() => setShowImport(false)}>Cancel</button>
              <button class="btn-resume" onClick={handleImport} disabled={!importText.trim()}>
                Next: Set Scope
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
